import { computed, inject, Injectable, signal } from '@angular/core';
import { injectQuery } from '@tanstack/angular-query-experimental';
import { lastValueFrom } from 'rxjs';

import { Auth } from 'src/providers/auth/auth';

import { DashboardWidgetsApiService } from './dashboard-widgets-api.service';
import { MonthAttributionService } from './month-attribution.service';
import {
  FinanceKpisData,
  FinanceSummaryData,
  FinanceTrendsData,
  FinanceWidgetEnvelope,
  FinanceWidgetsRequest,
  MonthAttribution,
  OperationsKpisData,
} from 'src/models/monthly-finance.model';

export const dashboardWidgetsKeys = {
  all: ['dashboard-widgets'] as const,
  finance: (addressId: number | null, startDate: string | null, endDate: string | null, year: number | null, attribution: MonthAttribution) =>
    [...dashboardWidgetsKeys.all, 'finance', addressId, startDate, endDate, year, attribution] as const,
  operations: (addressId: number | null, startDate: string | null, endDate: string | null) =>
    [...dashboardWidgetsKeys.all, 'operations', addressId, startDate, endDate] as const,
};

@Injectable({ providedIn: 'root' })
export class DashboardWidgetsQueriesService {
  private readonly api = inject(DashboardWidgetsApiService);
  private readonly auth = inject(Auth);
  private readonly monthAttribution = inject(MonthAttributionService);

  readonly addressId = signal<number | null>(null);
  readonly startDate = signal<string | null>(null);
  readonly endDate = signal<string | null>(null);
  readonly year = signal<number | null>(null);

  readonly financeWidgetsQuery = injectQuery(() => {
    const addressId = this.addressId();
    const startDate = this.startDate();
    const endDate = this.endDate();
    const year = this.year();
    const attribution = this.monthAttribution.value();
    return {
      queryKey: dashboardWidgetsKeys.finance(addressId, startDate, endDate, year, attribution),
      queryFn: () => lastValueFrom(this.api.fetchWidgets(this.buildRequest(addressId, startDate, endDate, year, attribution))),
      staleTime: 60 * 1000,
      enabled: this.auth.isAuthenticated() && startDate !== null && endDate !== null && year !== null,
    };
  });

  // Fetched on its own: the backend computes a request's widgets one after another, and
  // operations_kpis waits on the Chat Service, so sharing a request would hold up the money tiles.
  readonly operationsWidgetsQuery = injectQuery(() => {
    const addressId = this.addressId();
    const startDate = this.startDate();
    const endDate = this.endDate();
    return {
      queryKey: dashboardWidgetsKeys.operations(addressId, startDate, endDate),
      queryFn: () => lastValueFrom(this.api.fetchWidgets(this.buildOperationsRequest(addressId, startDate, endDate))),
      staleTime: 60 * 1000,
      // operations_kpis rejects a request without an address, so wait for the property to resolve.
      enabled: this.auth.isAuthenticated() && addressId !== null && startDate !== null && endDate !== null,
    };
  });

  readonly envelopes = computed<FinanceWidgetEnvelope[]>(() => this.financeWidgetsQuery.data() ?? []);
  private readonly operationsEnvelopes = computed<FinanceWidgetEnvelope[]>(
    () => this.operationsWidgetsQuery.data() ?? [],
  );

  readonly kpis = computed<FinanceKpisData | null>(
    () => (this.envelopes().find(e => e.key === 'finance_kpis')?.data as FinanceKpisData | undefined) ?? null,
  );
  readonly trends = computed<FinanceTrendsData | null>(
    () => (this.envelopes().find(e => e.key === 'finance_trends')?.data as FinanceTrendsData | undefined) ?? null,
  );
  readonly summary = computed<FinanceSummaryData | null>(
    () => (this.envelopes().find(e => e.key === 'finance_summary')?.data as FinanceSummaryData | undefined) ?? null,
  );
  readonly opsKpis = computed<OperationsKpisData | null>(
    () =>
      (this.operationsEnvelopes().find(e => e.key === 'operations_kpis')?.data as OperationsKpisData | undefined) ??
      null,
  );

  readonly isLoading = computed(() => this.financeWidgetsQuery.isPending());

  private buildRequest(
    addressId: number | null,
    startDate: string | null,
    endDate: string | null,
    year: number | null,
    attribution: MonthAttribution,
  ): FinanceWidgetsRequest {
    const req: FinanceWidgetsRequest = {};
    const addr = addressId ?? undefined;
    if (startDate && endDate) {
      req.finance_kpis = { start_date: startDate, end_date: endDate, address_id: addr, month_attribution: attribution };
      req.finance_trends = { start_date: startDate, end_date: endDate, address_id: addr, month_attribution: attribution };
    }
    if (year !== null) {
      req.finance_summary = { year, address_id: addr, month_attribution: attribution };
    }
    return req;
  }

  private buildOperationsRequest(
    addressId: number | null,
    startDate: string | null,
    endDate: string | null,
  ): FinanceWidgetsRequest {
    if (!startDate || !endDate) {return {};}
    return { operations_kpis: { start_date: startDate, end_date: endDate, address_id: addressId ?? undefined } };
  }
}
